/*
 * Copyright (c) 2024-2025 Hardfin, Inc.
 */
import { syncHubspotProperties } from '@/account/api/crm-property-mapping';
import {
  HubspotConfiguration,
  HubspotPropertyConfiguration,
} from '@/account/api/types';
import { syncHubspotPipelineConfig } from '@/account/api/update-hubspot-pipeline-config';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useSupportError } from '@/hooks/use-support-error';
import { Button } from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface PipelineProps {
  queryKey: readonly string[];
  setErrorMessage: (msg: string) => void;
  notifyRefresh: (state: boolean) => void;
  onRefresh: (data: HubspotConfiguration) => void;
}

export function CRMResyncPipelineHubspot(props: PipelineProps) {
  const queryClient = useQueryClient();
  const { showSupportError, hideSupportError } = useSupportError();
  const updateHubspotPipelineConfigMutation = useMutation({
    mutationFn: () => {
      props.notifyRefresh(true);
      return syncHubspotPipelineConfig();
    },
    onSuccess: (data) => {
      // Update the query client with the latest mutation
      queryClient.setQueryData(props.queryKey, () => data.data);
      props.onRefresh(data.data);
      props.notifyRefresh(false);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const safe = getUserFacingErrorMessage(apiError);
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      props.setErrorMessage('');
      hideSupportError();
      if (safe === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('Failed to resync HubSpot pipeline configuration.');
      } else {
        props.setErrorMessage(safe);
      }
      props.notifyRefresh(false);
    },
  });

  return (
    <Button
      disabled={updateHubspotPipelineConfigMutation.isPending}
      onClick={() => {
        if (updateHubspotPipelineConfigMutation.isPending) {
          return;
        }
        updateHubspotPipelineConfigMutation.mutate();
      }}
    >
      {updateHubspotPipelineConfigMutation.isPending
        ? 'Refreshing...'
        : 'Refresh'}
    </Button>
  );
}

interface PropertiesProps {
  queryKey: readonly string[];
  setErrorMessage: (msg: string) => void;
  notifyRefresh: (state: boolean) => void;
  propertyId: string;
  onRefresh: (data: HubspotPropertyConfiguration) => void;
}

export function CRMResyncPropertiesHubspot(props: PropertiesProps) {
  const queryClient = useQueryClient();
  const { showSupportError, hideSupportError } = useSupportError();
  const syncHubspotPropertiesMutation = useMutation({
    mutationFn: () => {
      props.notifyRefresh(true);
      return syncHubspotProperties(props.propertyId);
    },
    onSuccess: (data) => {
      // Update the query client with the latest mutation
      queryClient.setQueryData(props.queryKey, () => data.data);
      props.onRefresh(data.data);
      props.notifyRefresh(false);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const safe = getUserFacingErrorMessage(apiError);
      // Reset both surfaces so a retry never leaves the prior global
      // support alert visible alongside the new inline error.
      props.setErrorMessage('');
      hideSupportError();
      if (safe === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError('Failed to resync HubSpot properties.');
      } else {
        props.setErrorMessage(safe);
      }
      props.notifyRefresh(false);
    },
  });

  return (
    <Button
      disabled={syncHubspotPropertiesMutation.isPending}
      onClick={() => {
        if (syncHubspotPropertiesMutation.isPending) {
          return;
        }
        syncHubspotPropertiesMutation.mutate();
      }}
    >
      {syncHubspotPropertiesMutation.isPending ? 'Refreshing...' : 'Refresh'}
    </Button>
  );
}
